import React, { useEffect, useState, useCallback } from "react";
import "./attendance.css";
import "../AddWorkReport/work_report.css";
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Target,
  LogIn,
  LogOut,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  addAttendanceLog,
  getMyAttendanceLogs,
  addTodayAttendance,
} from "../../service/attendance_service";

const Attendance = () => {
  const [checkedIn, setCheckedIn] = useState(false);
  const [checkInTime, setCheckInTime] = useState("--:--");
  const [checkOutTime, setCheckOutTime] = useState("--:--");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [submitLoading, setSubmitLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const getDaysInMonth = (year, month) =>
    new Date(year, month + 1, 0).getDate();
  const getStartDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  const formatTime = (timeStr) => {
    // Converts HH:MM or HH.MM to HH.MM string
    if (!timeStr || timeStr === "--:--") return "--:--";
    return timeStr.replace(":", ".");
  };

  const getHHMMAsNumber = (timeStr) => {
    if (!timeStr || timeStr === "--:--") return 0;
    const formatted = formatTime(timeStr);
    return parseFloat(formatted);
  };

  const calculateWorkingHours = (inTimeStr, outTimeStr) => {
    if (
      !inTimeStr ||
      inTimeStr === "--:--" ||
      !outTimeStr ||
      outTimeStr === "--:--"
    )
      return 0;

    const [inH, inM] = inTimeStr.replace(":", ".").split(".").map(Number);
    const [outH, outM] = outTimeStr.replace(":", ".").split(".").map(Number);

    const inTotalMins = inH * 60 + inM;
    const outTotalMins = outH * 60 + outM;

    if (outTotalMins <= inTotalMins) return 0;

    const diffMins = outTotalMins - inTotalMins;
    const h = Math.floor(diffMins / 60);
    const m = diffMins % 60;

    return parseFloat(`${h}.${m.toString().padStart(2, "0")}`);
  };
  const [attendanceData, setAttendanceData] = useState({});
  const [stats, setStats] = useState({
    present: 0,
    absent: 0,
    workingHours: 0,
    punctuality: 0,
  });

  const fetchAttendanceLogs = useCallback(async () => {
    try {
      const response = await getMyAttendanceLogs();
      if (response.status === "success" && response.data) {
        const dataMap = {};
        let presentCount = 0;
        let absentCount = 0;
        let totalHours = 0;

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        let foundToday = false;

        response.data.forEach((log) => {
          const logDate = new Date(log.date);
          const logDateOnly = new Date(log.date);
          logDateOnly.setHours(0, 0, 0, 0);

          if (
            logDate.getMonth() === currentDate.getMonth() &&
            logDate.getFullYear() === currentDate.getFullYear()
          ) {
            dataMap[logDate.getDate()] = log.status.toLowerCase();
            if (log.status === "Present") presentCount++;
            if (log.status === "Absent") absentCount++;
            if (log.workinghours) totalHours += parseFloat(log.workinghours);
          }

          // Synchronize today's status indicators
          if (logDateOnly.getTime() === today.getTime()) {
            foundToday = true;
            if (log.checkin && parseFloat(log.checkin) !== 0) {
              const checkInStr = log.checkin.toString();
              setCheckInTime(checkInStr);
              localStorage.setItem("checkIn", checkInStr);

              if (log.checkout && parseFloat(log.checkout) !== 0) {
                setCheckOutTime(log.checkout.toString());
                setCheckedIn(false);
              } else {
                setCheckOutTime("--:--");
                setCheckedIn(true);
              }
            } else {
              setCheckInTime("--:--");
              setCheckOutTime("--:--");
              setCheckedIn(false);
              localStorage.removeItem("checkIn");
            }
          }
        });

        if (!foundToday) {
          setCheckInTime("--:--");
          setCheckOutTime("--:--");
          setCheckedIn(false);
          localStorage.removeItem("checkIn");
        }

        setAttendanceData(dataMap);
        const totalTrackedDays = presentCount + absentCount;
        setStats({
          present: presentCount,
          absent: absentCount,
          workingHours: totalHours.toFixed(2),
          punctuality:
            totalTrackedDays > 0
              ? ((presentCount / totalTrackedDays) * 100).toFixed(1)
              : "0",
        });
      }
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    setLoading(true);
    fetchAttendanceLogs();
  }, [fetchAttendanceLogs]);

  const generateCalendarDays = () => {
    const days = [];
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const startDay = getStartDayOfMonth(year, month);

    const today = new Date();
    const isCurrentMonth =
      today.getFullYear() === year && today.getMonth() === month;

    for (let i = 1; i <= daysInMonth; i++) {
      const dayOfWeek = (i + startDay - 1) % 7;
      const weekNumber = Math.ceil((i + startDay) / 7);

      let status = attendanceData[i];

      // Sunday is index 0
      const isSunday = dayOfWeek === 0;
      // Saturday is index 6
      const isSatOff =
        dayOfWeek === 6 && (weekNumber === 2 || weekNumber === 4);

      const isOffDay = isSunday || isSatOff;
      const isToday = isCurrentMonth && today.getDate() === i;

      days.push(
        <div
          key={`${year}-${month}-${i}`}
          className={`cal-date-cell ${isToday ? "today" : ""} ${isOffDay ? "off-day" : status || ""}`}
        >
          {i}
          {!isOffDay && (
            <>
              {status === "present" && (
                <span className="cal-mark present" title="Present"></span>
              )}
              {status === "absent" && (
                <span className="cal-mark absent" title="Absent"></span>
              )}
            </>
          )}
        </div>,
      );
    }
    return days;
  };

  const generateEmptyCells = () => {
    const startDay = getStartDayOfMonth(
      currentDate.getFullYear(),
      currentDate.getMonth(),
    );
    return [...Array(startDay)].map((_, i) => (
      <div
        key={`empty-${i}`}
        className="cal-date-cell empty-cell"
        style={{ visibility: "hidden" }}
      ></div>
    ));
  };

  const handleCheckIn = async () => {
    const timeStr = new Date().toTimeString().slice(0, 5).replace(":", ".");
    const success = await addTodayAttendanceData(timeStr);

    if (success) {
      setCheckInTime(timeStr);
      localStorage.setItem("checkIn", timeStr);
      setCheckedIn(true);
    }
  };

  const handleCheckOut = async () => {
    const outTime = new Date().toTimeString().slice(0, 5).replace(":", ".");
    setCheckOutTime(outTime);
    await addAttendanceLogData(checkInTime, outTime);
  };

  /* API Functions */

  const addAttendanceLogData = async (inTime, outTime) => {
    if (!inTime || inTime === "--:--") {
      toast.error("Check-in record not found. Please refresh and try again.");
      return;
    }
    setSubmitLoading(true);
    try {
      const checkInValue = getHHMMAsNumber(inTime);
      const checkOutValue = getHHMMAsNumber(outTime);
      const workingHours = calculateWorkingHours(inTime, outTime);

      const response = await addAttendanceLog({
        checkin: checkInValue,
        checkout: checkOutValue,
        workinghours: workingHours,
      });

      if (response.status === "success") {
        toast.success("Check-out successful");
        localStorage.removeItem("checkIn");
        setCheckedIn(false);
        setCheckInTime("--:--");
        setCheckOutTime("--:--");
        fetchAttendanceLogs();
      } else {
        toast.error(response.message || "Failed to add attendance");
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setSubmitLoading(false);
    }
  };

  const addTodayAttendanceData = async (inTime) => {
    setSubmitLoading(true);
    try {
      const checkInValue = getHHMMAsNumber(inTime);
      await addTodayAttendance({
        checkin: checkInValue,
      });
      toast.success("Check-in successful");
      fetchAttendanceLogs();
      return true;
    } catch (error) {
      toast.error(error.message || "Failed to check in");
      return false;
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <div className="add-report-container">
      <div className="page-top-header">
        <h4>Attendance Dashboard</h4>
      </div>
      <div className="report-detail-grid-new">
        {loading ? (
          <div className="loading-state">
            <div className="loader"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {/* Top Section: Check-in/out Actions */}
            <div className="check-in-grid">
              <div
                className={`attendance-status-card ${checkedIn ? "active-session" : ""}`}
              >
                <div className="card-indicator"></div>
                <div className="status-header">
                  <div className="status-icon-box in">
                    <LogIn size={22} strokeWidth={2.5} />
                  </div>
                  <div className="status-badge pulse">
                    {checkedIn ? "Session Active" : "Waiting for Check-in"}
                  </div>
                </div>
                <div className="status-content">
                  <p className="status-label">Check-in Time</p>
                  <h4 className="status-time">{formatTime(checkInTime)}</h4>
                </div>
                <button
                  className="modern-action-btn in-btn"
                  onClick={handleCheckIn}
                  disabled={
                    checkedIn ||
                    submitLoading ||
                    (checkInTime !== "--:--" && checkOutTime !== "--:--")
                  }
                  style={{
                    opacity:
                      checkedIn ||
                        submitLoading ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? 0.6
                        : 1,
                    cursor:
                      checkedIn ||
                        submitLoading ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  <div className="btn-effect"></div>
                  <span>
                    {submitLoading
                      ? "Processing..."
                      : checkedIn
                        ? "CHECKED IN"
                        : "CHECK IN"}
                  </span>
                </button>
              </div>

              <div className="attendance-status-card checkout-card">
                <div className="card-indicator"></div>
                <div className="status-header">
                  <div className="status-icon-box out">
                    <LogOut size={22} strokeWidth={2.5} />
                  </div>
                  {checkOutTime !== "--:--" && !checkedIn && (
                    <div className="status-badge success">CHECK OUT</div>
                  )}
                </div>
                <div className="status-content">
                  <p className="status-label">Check-out Time</p>
                  <h4 className="status-time">{formatTime(checkOutTime)}</h4>
                </div>
                <button
                  className="modern-action-btn out-btn"
                  onClick={handleCheckOut}
                  style={{
                    opacity:
                      !checkedIn ||
                        submitLoading ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? 0.6
                        : 1,
                    cursor:
                      !checkedIn ||
                        submitLoading ||
                        (checkInTime !== "--:--" && checkOutTime !== "--:--")
                        ? "not-allowed"
                        : "pointer",
                  }}
                  disabled={
                    !checkedIn ||
                    submitLoading ||
                    (checkInTime !== "--:--" && checkOutTime !== "--:--")
                  }
                >
                  <div className="btn-effect"></div>
                  <span>{submitLoading ? "Processing..." : "CHECKED OUT"}</span>
                </button>
              </div>
            </div>

            {/* Middle Section: Calendar & Insights side-by-side */}
            <div className="calendar-insights-wrapper">
              <div className="calendar-container-card" style={{ margin: 0 }}>
                <div className="cal-header">
                  <div className="cal-title-wrap">
                    <div
                      className="header-icon-box"
                      style={{
                        background: "#f0f9ff",
                        padding: "8px",
                        borderRadius: "10px",
                      }}
                    >
                      <CalendarDays size={24} color="#0ea5e9" />
                    </div>
                    <div>
                      <h3 style={{ fontSize: "18px", fontWeight: "700" }}>
                        Attendance Calendar
                      </h3>
                      <p style={{ fontSize: "12px", color: "#64748b", margin: 0 }}>
                        Showing data for{" "}
                        {currentDate.toLocaleString("default", { month: "long" })}{" "}
                        {currentDate.getFullYear()}
                      </p>
                    </div>
                  </div>
                  <div className="calendar-controls">
                    <button className="nav-btn" onClick={handlePrevMonth}>
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      className="nav-btn"
                      style={{
                        fontSize: "12px",
                        width: "auto",
                        padding: "0 15px",
                        fontWeight: "700",
                      }}
                    >
                      {currentDate.toLocaleString("default", { month: "short" })}{" "}
                      {currentDate.getFullYear()}
                    </button>
                    <button className="nav-btn" onClick={handleNextMonth}>
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <div className="cal-grid-layout">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div key={day} className="cal-day-label">
                      {day}
                    </div>
                  ))}

                  {generateEmptyCells()}
                  {generateCalendarDays()}
                </div>

                <div className="cal-legend-row">
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#10b981" }}
                    ></div>
                    Present
                  </div>
                  <div className="cal-legend-item">
                    <div
                      className="cal-legend-dot"
                      style={{ background: "#ef4444" }}
                    ></div>
                    Absent
                  </div>
                </div>
              </div>

              <div
                className="sidebar-info-card"
                style={{
                  borderTop: "4px solid #0ea5e9",
                  margin: 0,
                  height: "100%",
                }}
              >
                <h5 style={{ border: "none", padding: 0 }}>
                  <Target size={18} color="#0ea5e9" />
                  Monthly Insights
                </h5>
                <div style={{ marginTop: "5px" }}>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Punctuality Score</span>
                    <span className="stats-value" style={{ color: "#10b981" }}>
                      {`${((stats.present / 30) * 100).toFixed(2)}%`}
                    </span>
                  </div>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Working Hours</span>
                    <span className="stats-value">{stats.workingHours} Hrs</span>
                  </div>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Present Days</span>
                    <span className="stats-value">
                      {stats.present < 10 ? `0${stats.present}` : stats.present}
                    </span>
                  </div>
                  <div className="sidebar-stats-item">
                    <span className="stats-label">Absent Days</span>
                    <span className="stats-value">
                      {stats.absent < 10 ? `0${stats.absent}` : stats.absent}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        <div style={{ height: "40px" }}></div>
      </div>
    </div>
  );
};

export default Attendance;
